/**
 * ServiceRealEstate.tsx
 * 
 * Design Philosophy: Editorial Minimalism with Architectural Precision
 * - Clean typography hierarchy with generous whitespace
 * - Muted earth tones (warm beige, soft grey, deep burgundy accents)
 * - Structured grid layouts with asymmetric photo placements
 * - Subtle shadows and borders for depth without heaviness
 */

import { Link } from "wouter";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Home, Search, FileText, Key, CheckCircle2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

const HERO_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/re-hero-exterior-6FYDYzrGPV93CKGy9ittZR.webp";
const INTERIOR_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/re-interior-living-3BogqYivCmT8SbbCnzRjeM.webp";

export default function ServiceRealEstate() {
  return (
    <>
    <Helmet>
      <title>Real Estate Services in Brașov — Find Your Apartment in Romania</title>
      <meta name="description" content="Find your apartment or house in Brașov with local expert guidance. We handle property search, legal checks, negotiation, and purchase — end to end." />
      <meta property="og:title" content="Real Estate Services in Brașov — Find Your Apartment in Romania" />
      <meta property="og:description" content="Find your apartment or house in Brașov with local expert guidance. We handle property search, legal checks, negotiation, and purchase — end to end." />
      <meta property="og:image" content="https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-real-estate-oVkqv2kDQCBzogKNZUAGJw.webp" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://brasovgateway.com/services/real-estate" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Real Estate Services in Brașov — Find Your Apartment in Romania" />
      <link rel="canonical" href="https://brasovgateway.com/services/real-estate" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "Service",
          "@id": "https://brasovgateway.com/services/real-estate",
          "name": "Real Estate Advisory in Brașov",
          "url": "https://brasovgateway.com/services/real-estate",
          "description": "End-to-end real estate advisory for foreigners buying property in Brașov, Romania — neighbourhood selection, developer vetting, legal support, and negotiation.",
          "provider": {
            "@type": "Organization",
            "name": "Brașov Gateway",
            "url": "https://brasovgateway.com"
          },
          "areaServed": {
            "@type": "City",
            "name": "Brașov"
          },
          "serviceType": "Real Estate Advisory"
        }
      `}</script>
    </Helmet>
    <div className="min-h-screen bg-background">
      <SiteHeader />
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        </div>
        
        <div className="container relative z-10 text-center text-white px-4">
          <p className="text-sm uppercase tracking-[0.2em] mb-4 text-white/90">Services</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif mb-6">
            Real Estate in Brașov
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed text-white/90">
            From property search to keys in hand — expert guidance for buying your home in Transylvania's most livable city.
          </p>
        </div>
      </section>

      {/* Why Brașov Section */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-serif mb-8 text-foreground">
              Why Brașov real estate?
            </h2>
            <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
              <p>
                Over the past decade, Brașov has transformed from a quiet mountain town into one of Romania's most sought-after residential markets. Prices have climbed steadily — not because of speculation, but because people genuinely want to live here.
              </p>
              <p>
                The reasons are straightforward: clean air, proximity to nature, a well-preserved medieval center, and infrastructure that's improving year by year. It's close enough to Bucharest (2.5 hours) to stay connected, yet far enough to feel like a different world.
              </p>
              <p>
                Whether you're relocating for work, retiring to the mountains, or investing in rental property, Brașov offers a rare combination of quality of life and long-term value.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-serif mb-12 text-center">
            What I help you with
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Search,
                title: "Property Search",
                description: "I find apartments, houses, and land that match your criteria — budget, location, size, and lifestyle needs."
              },
              {
                icon: Home,
                title: "Viewings & Evaluation",
                description: "I arrange viewings, point out what matters (and what doesn't), and help you assess quality and fair market value."
              },
              {
                icon: FileText,
                title: "Price Negotiation",
                description: "I negotiate on your behalf to secure the best possible price, backed by local market knowledge."
              },
              {
                icon: FileText,
                title: "Legal Assistance",
                description: "I coordinate with notaries and lawyers to ensure contracts are clean, ownership is clear, and the process is smooth."
              },
              {
                icon: Key,
                title: "Handover & Setup",
                description: "I assist with utility setup, minor repairs, and connecting you with trusted contractors for renovations or furnishing."
              },
              {
                icon: MessageSquare,
                title: "Post-Purchase Support",
                description: "Even after the keys are handed over, I'm available for questions about property management, taxes, or local services."
              }
            ].map((service, idx) => (
              <div key={idx} className="bg-card p-8 rounded-sm border border-border hover:shadow-lg transition-shadow">
                <service.icon className="w-10 h-10 mb-4 text-primary" />
                <h3 className="text-xl font-semibold mb-3 text-card-foreground">{service.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-serif mb-16 text-center">
            The process, step by step
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-12">
            {[
              {
                step: "01",
                title: "Initial Consultation",
                description: "We discuss your budget, preferences, timeline, and any concerns. I explain the local market and set realistic expectations."
              },
              {
                step: "02",
                title: "Property Search",
                description: "I search listings, contact agents, and compile a shortlist of properties that fit your criteria."
              },
              {
                step: "03",
                title: "Viewings",
                description: "We visit properties together. I point out structural issues, neighborhood dynamics, and potential red flags."
              },
              {
                step: "04",
                title: "Offer & Negotiation",
                description: "Once you've chosen a property, I help you make a competitive offer and negotiate the final price."
              },
              {
                step: "05",
                title: "Legal & Contracts",
                description: "I coordinate with notaries and lawyers to verify ownership, draft contracts, and ensure everything is legally sound."
              },
              {
                step: "06",
                title: "Keys & Handover",
                description: "After signing, I assist with the handover, utility setup, and any immediate repairs or improvements."
              }
            ].map((item, idx) => (
              <div key={idx} className="flex gap-8 items-start">
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-2xl font-serif text-primary">{item.step}</span>
                </div>
                <div className="flex-1 pt-2">
                  <h3 className="text-2xl font-semibold mb-3 text-foreground">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interior Image Section */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <img 
              src={INTERIOR_IMG} 
              alt="Modern apartment interior with mountain view" 
              className="w-full h-auto rounded-sm shadow-xl mb-8"
            />
            <p className="text-center text-muted-foreground italic">
              Modern apartments in Brașov offer mountain views, natural light, and contemporary design.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif mb-6">
              Ready to find your place in Brașov?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Whether you're buying your first apartment or investing in rental property, I'll guide you through every step.
            </p>
            <Link href="/contact">
              <Button size="lg" className="group">
                Schedule a Consultation
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Article */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <p className="text-sm uppercase tracking-[0.2em] mb-4 text-muted-foreground">Related Reading</p>
            <h3 className="text-2xl md:text-3xl font-serif mb-4">
              <Link href="/buying-apartment-brasov" className="hover:text-primary transition-colors">
                Brașov real estate: a decade of prices that kept going up — and why they're not done yet
              </Link>
            </h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              In roughly ten years, prices in Brașov have nearly tripled. The question worth asking is whether that curve is still going up, or whether you've already missed the train.
            </p>
            <Link href="/buying-apartment-brasov">
              <Button variant="outline" className="group">
                Read Article
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
    </>
  );
}
